import { Formik } from "formik";
import { useParams } from "react-router-dom";
import * as Yup from "yup";

import { HelpRounded } from "@mui/icons-material";
import SaveIcon from "@mui/icons-material/Save";
import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import CircularProgress from "@mui/material/CircularProgress";
import Tooltip from "@mui/material/Tooltip";
import Typography from "@mui/material/Typography";

import CustomSelect from "@components/Forms/FormInputs/CustomSelect";
import TextField from "@components/Forms/FormInputs/TextField";
import {
  TableToolbar,
  TableToolbarLeft,
  TableToolbarRight,
  TableToolbarTitle,
} from "@components/TableToolbar/TableToolbar";

import { useUpdateOrganization } from "@queries/organization.query";

import { useOrganizationStore } from "@store/organizationStore";

import theme from "@theme/index";

const llmModels = [
  { label: "OpenAI - Chat GPT Enterprise", value: "openai" },
  { label: "Anthropic - Claude Enterprise", value: "anthropic" },
];

const editOrganizationNameSchema = Yup.object().shape({
  teamName: Yup.string().required("Required"),
});

export default function OrganizationSettings() {
  const organization = useOrganizationStore((state) => state.organization);
  const { mutateAsync: updateOrganization } = useUpdateOrganization();
  const setOrganization = useOrganizationStore((state) => state.actions.setOrganization);
  const { organizationId } = useParams();

  if (!organization || !organizationId) {
    return null;
  }

  return (
    <>
      <Formik
        enableReinitialize
        initialValues={{
          chatModel: organization.chatModel,
          teamName: organization.teamName,
        }}
        onSubmit={async (values, { setSubmitting }) => {
          try {
            setOrganization({
              ...organization,
              chatModel: values.chatModel,
              teamName: values.teamName,
            });
            await updateOrganization({
              organizationId: organizationId,
              ...values,
            });
          } finally {
            setSubmitting(false);
          }
        }}
        validationSchema={editOrganizationNameSchema}
      >
        {({ dirty, handleSubmit, isSubmitting, isValid, setFieldValue, values }) => (
          <>
            <TableToolbar>
              <TableToolbarLeft>
                <TableToolbarTitle resourceLink="" tableSubtitle="" tableTitle="Settings" workflow="" />
              </TableToolbarLeft>
              <TableToolbarRight>
                <Button
                  disabled={!isValid || !dirty || isSubmitting}
                  onClick={() => handleSubmit()}
                  startIcon={<SaveIcon />}
                  variant="contained"
                >
                  {isSubmitting ? <CircularProgress size={20} /> : "Update"}
                </Button>
              </TableToolbarRight>
            </TableToolbar>
            <Box sx={{ p: 3 }}>
              <Box sx={{ display: "flex", gap: 4 }}>
                <Box sx={{ flex: 1 }}>
                  <Typography fontWeight={700} sx={{ mb: 1 }} variant="body1">
                    Organization Name
                  </Typography>
                  <TextField
                    label=""
                    name="teamName"
                    sx={{
                      "& .MuiOutlinedInput-input": {
                        backgroundColor: theme.palette.common.white,
                      },
                      "& .MuiOutlinedInput-notchedOutline": {
                        borderRadius: "8px",
                      },
                      borderRadius: "8px",
                      height: "56px",
                      margin: 0,
                      width: "100%",
                    }}
                    type="text"
                  />
                </Box>
                <Box sx={{ flex: 1 }}>
                  <Typography fontWeight={700} sx={{ alignItems: "center", display: "flex", mb: 1 }} variant="body1">
                    <span>AI Model</span>
                    <Tooltip
                      placement="bottom"
                      title="Choose an AI model to generate insights and marketing summaries."
                    >
                      <HelpRounded
                        sx={{
                          color: theme.palette.info.main,
                          cursor: "help",
                          fontWeight: "bold",
                          ml: 0.5,
                          width: 18,
                        }}
                      />
                    </Tooltip>
                  </Typography>
                  <CustomSelect
                    customRenderValue={() =>
                      llmModels
                        .filter((model) => model.value === values.chatModel)
                        .map((model) => model.label)
                        .join(", ")
                    }
                    defaultSelectedOptions={[]}
                    formControlSx={{
                      alignContent: "center",
                      width: "100%",
                    }}
                    getOptionLabel={(model) => model.label}
                    getOptionValue={(model) => model.value}
                    isMultiSelect={false}
                    margin="none"
                    onChange={(newValue) => void setFieldValue("chatModel", newValue[0])}
                    options={llmModels}
                    selectedValues={[values.chatModel ?? "openai"]}
                    // TODO: Standardize border radius, width and other toolbar styles so that we are not tracking borderRadius in multiple places.
                    sx={{ borderRadius: "8px", height: "56px" }}
                  />
                </Box>
              </Box>
            </Box>
          </>
        )}
      </Formik>
    </>
  );
}
